// Dungeons (stage 7): one per ring, carved into the deep-sea corners of the map so they reuse the
// world's chunks, monsters, lighting and pathing. Each is a chain of rooms ending in a boss arena,
// reached only through a portal standing somewhere in its ring. Pure and seeded, like worldgen.
import { G, MAP_H, MAP_W, OBJ, groundSolid } from './content.ts';
import type { ItemId } from './items.ts';
import { mulberry32 } from './noise.ts';

export const DUNGEON_SIZE = 100;

export type Room = { x: number; y: number; w: number; h: number };

export type DungeonDef = {
  ring: number;
  name: string;
  boss: string; // monster slug (shared/monsters.ts)
  floor: number; // ground id
  /** Top-left corner of the carved area. */
  ox: number;
  oy: number;
  /**
   * Portal realms (stage 8): endgame places past the four dungeons, carved into the deep sea along
   * the top edge. Their portal stands in `portalRing` and only lets you through with `key` in your
   * pack. `power` scales their monsters' health and damage.
   */
  realm?: { layout: 'maze' | 'glade'; key: ItemId; portalRing: number; power: number; wall: number; blurb: string; pack: string[]; elites: string[] };
};

export const DUNGEONS: DungeonDef[] = [
  { ring: 0, name: 'The Barrow', boss: 'boss_knight', floor: G.BARROW, ox: 8, oy: 8 },
  { ring: 1, name: 'The Sunken Crypt', boss: 'boss_slime', floor: G.CRYPT, ox: MAP_W - 8 - DUNGEON_SIZE, oy: 8 },
  { ring: 2, name: 'The Deep Mine', boss: 'boss_minotaur', floor: G.DEEP_MINE, ox: 8, oy: MAP_H - 8 - DUNGEON_SIZE },
  { ring: 3, name: 'The Demon Gate', boss: 'boss_demon', floor: G.HELL_FLOOR, ox: MAP_W - 8 - DUNGEON_SIZE, oy: MAP_H - 8 - DUNGEON_SIZE },
  // Realms: always after the dungeons in this list (their portals use their own random stream).
  { ring: 3, name: 'The Hollow', boss: 'boss_lich', floor: G.HOLLOW, ox: 116, oy: 8,
    realm: { layout: 'maze', key: 'hollow_key', portalRing: 2, power: 1.3, wall: G.CAVE_WALL, blurb: 'A maze of bone and cold stone. The Lich waits at its heart.',
      pack: ['dw_necromancer', 'celtic_banshee', 'sanctum_warden_of_chains', 'sanctum_flesh_collector', 'sanctum_faceless_nurse', 'sanctum_mourning_bride'], elites: ['sanctum_hollow_king', 'sanctum_bell_ringer'] } },
  { ring: 3, name: 'The Twilight Glade', boss: 'boss_dragon', floor: G.GLADE, ox: MAP_W - 116 - DUNGEON_SIZE, oy: 8,
    realm: { layout: 'glade', key: 'glade_key', portalRing: 3, power: 1.7, wall: G.THICKET, blurb: 'A forest where the sun never quite sets. Something old and red sleeps in the deepest clearing.',
      pack: ['celtic_fae_executioner', 'dw_goblin_trapper', 'dw_lizard_spearman', 'dw_orc_dungeon_brute', 'sanctum_iron_butcher'], elites: ['celtic_fae_queen', 'dw_dungeon_minotaur'] } },
];

export type Dungeon = DungeonDef & {
  index: number;
  rooms: Room[]; // rooms[0] = entrance, last = boss arena
  /** The portal inside the entrance room, and where you arrive next to it. */
  exit: { tx: number; ty: number };
  arrive: { tx: number; ty: number };
  /** The portal out in the ring, and where you arrive next to it on the way back. */
  portal: { tx: number; ty: number };
  back: { tx: number; ty: number };
};

/** Which dungeon a tile is in, or -1. Cheap enough for the client to call every frame. */
export function dungeonAt(tx: number, ty: number): number {
  for (let k = 0; k < DUNGEONS.length; k++) {
    const d = DUNGEONS[k];
    if (tx >= d.ox && ty >= d.oy && tx < d.ox + DUNGEON_SIZE && ty < d.oy + DUNGEON_SIZE) return k;
  }
  return -1;
}

const ORES = [[OBJ.COPPER, OBJ.TIN], [OBJ.IRON, OBJ.COAL], [OBJ.SILVER, OBJ.GOLD], [OBJ.DARK_ORE, OBJ.OBSIDIAN]];
/** Snake through a 3x3 grid of cells so the rooms make one long path. */
const SNAKE: [number, number][] = [[0, 0], [1, 0], [2, 0], [2, 1], [1, 1], [0, 1], [0, 2], [1, 2], [2, 2]];
/** Ring radius (fraction of the half-map) where each ring's portal stands. */
const PORTAL_R = [0.19, 0.42, 0.64, 0.82];

/**
 * Carve every dungeon and stand a portal for each in its ring. `ruins` are kept clear of portals.
 * Called by worldgen after ruins are placed; uses its own random stream so ruins don't move.
 */
export function carveDungeons(seed: number, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array, ruins: { x: number; y: number; w: number; h: number }[]): Dungeon[] {
  const rng = mulberry32(seed + 77);
  const out: Dungeon[] = [];
  DUNGEONS.forEach((def, index) => {
    if (def.realm) return;
    const { ox, oy } = def;
    for (let y = oy; y < oy + DUNGEON_SIZE; y++) {
      for (let x = ox; x < ox + DUNGEON_SIZE; x++) {
        const i = y * MAP_W + x;
        ground[i] = G.CAVE_WALL;
        obj[i] = 0;
        ring[i] = def.ring;
      }
    }
    const cell = Math.floor(DUNGEON_SIZE / 3);
    const rooms: Room[] = SNAKE.map(([cx, cy], k) => {
      const boss = k === SNAKE.length - 1;
      const w = boss ? 24 : 11 + Math.floor(rng() * 8);
      const h = boss ? 22 : 10 + Math.floor(rng() * 8);
      const x = ox + cx * cell + 2 + Math.floor(rng() * (cell - w - 4));
      const y = oy + cy * cell + 2 + Math.floor(rng() * (cell - h - 4));
      return { x, y, w, h };
    });
    const floor = (x: number, y: number) => {
      const i = y * MAP_W + x;
      ground[i] = def.floor;
      obj[i] = 0;
    };
    for (const r of rooms) for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) floor(x, y);
    // Corridors, 3 wide: each room to the next, plus one shortcut that makes a loop.
    const mid = (r: Room) => [r.x + (r.w >> 1), r.y + (r.h >> 1)];
    const corridor = (a: Room, b: Room) => {
      const [ax, ay] = mid(a), [bx, by] = mid(b);
      const flip = rng() < 0.5;
      const [kx, ky] = flip ? [bx, ay] : [ax, by];
      for (const [x0, y0, x1, y1] of [[ax, ay, kx, ky], [kx, ky, bx, by]]) {
        for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) for (let d = -1; d <= 1; d++) floor(x, y0 + d);
        for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) for (let d = -1; d <= 1; d++) floor(x0 + d, y);
      }
    };
    for (let k = 1; k < rooms.length; k++) corridor(rooms[k - 1], rooms[k]);
    corridor(rooms[1], rooms[4]);

    // Furnish: braziers in the corners, pillars and rubble, and veins of this ring's ores.
    const put = (x: number, y: number, o: number) => {
      const i = y * MAP_W + x;
      if (ground[i] === def.floor && !obj[i]) obj[i] = o;
    };
    rooms.forEach((r, k) => {
      for (const [x, y] of [[r.x + 1, r.y + 1], [r.x + r.w - 2, r.y + 1], [r.x + 1, r.y + r.h - 2], [r.x + r.w - 2, r.y + r.h - 2]]) put(x, y, OBJ.BRAZIER);
      if (k === 0 || k === rooms.length - 1) return;
      const n = 2 + Math.floor(rng() * 3);
      for (let j = 0; j < n; j++) put(r.x + 2 + Math.floor(rng() * (r.w - 4)), r.y + 2 + Math.floor(rng() * (r.h - 4)), rng() < 0.5 ? OBJ.RUIN_PILLAR : OBJ.RUBBLE);
      const ores = 1 + Math.floor(rng() * 3);
      for (let j = 0; j < ores; j++) put(r.x + 2 + Math.floor(rng() * (r.w - 4)), r.y + 2 + Math.floor(rng() * (r.h - 4)), ORES[def.ring][Math.floor(rng() * 2)]);
    });
    const [ex, ey] = mid(rooms[0]);
    obj[ey * MAP_W + ex] = OBJ.PORTAL;
    const exit = { tx: ex, ty: ey };
    const arrive = { tx: ex, ty: ey + 2 };
    const portal = standPortal(rng, def.ring, ground, obj, ring, ruins, []);
    out.push({ ...def, index, rooms, exit, arrive, portal, back: { tx: portal.tx, ty: portal.ty + 2 } });
  });
  // Realms get their own random stream, so adding one never moves the dungeons or their portals.
  const rrng = mulberry32(seed + 91);
  DUNGEONS.forEach((def, index) => {
    if (!def.realm) return;
    const rooms = def.realm.layout === 'maze' ? carveMaze(rrng, def, ground, obj, ring) : carveGlade(rrng, def, ground, obj, ring);
    const r0 = rooms[0];
    const ex = r0.x + (r0.w >> 1), ey = r0.y + (r0.h >> 1);
    obj[ey * MAP_W + ex] = OBJ.PORTAL;
    const portal = standPortal(rrng, def.realm.portalRing, ground, obj, ring, ruins, out.map((d) => d.portal));
    out.push({ ...def, index, rooms, exit: { tx: ex, ty: ey }, arrive: { tx: ex, ty: ey + 2 }, portal, back: { tx: portal.tx, ty: portal.ty + 2 } });
  });
  return out;
}

/** The way in: a stone ring in the right band of the island, away from ruins and other portals. */
function standPortal(rng: () => number, band: number, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array, ruins: { x: number; y: number; w: number; h: number }[], others: { tx: number; ty: number }[]): { tx: number; ty: number } {
  let portal = { tx: MAP_W / 2, ty: MAP_H / 2 + 6 };
  for (let tries = 0; tries < 2000; tries++) {
    const a = rng() * Math.PI * 2;
    const rad = PORTAL_R[band] * (MAP_W / 2) * (0.95 + rng() * 0.1);
    const tx = Math.round(MAP_W / 2 + Math.cos(a) * rad), ty = Math.round(MAP_H / 2 + Math.sin(a) * rad);
    let ok = ring[ty * MAP_W + tx] === band;
    for (let y = ty - 3; y <= ty + 3 && ok; y++) for (let x = tx - 3; x <= tx + 3 && ok; x++) if (groundSolid(ground[y * MAP_W + x]) || ground[y * MAP_W + x] === G.SHALLOW) ok = false;
    if (ok && ruins.some((r) => tx > r.x - 8 && tx < r.x + r.w + 8 && ty > r.y - 8 && ty < r.y + r.h + 8)) ok = false;
    if (ok && others.some((o) => Math.hypot(o.tx - tx, o.ty - ty) < 40)) ok = false;
    if (!ok) continue;
    portal = { tx, ty };
    break;
  }
  for (let y = portal.ty - 3; y <= portal.ty + 3; y++) {
    for (let x = portal.tx - 3; x <= portal.tx + 3; x++) {
      const d = Math.hypot(x - portal.tx, y - portal.ty);
      const i = y * MAP_W + x;
      if (d <= 3.2) obj[i] = 0;
      if (d <= 2.3) ground[i] = G.STONE;
    }
  }
  obj[portal.ty * MAP_W + portal.tx] = OBJ.PORTAL;
  return portal;
}

function fillRealm(def: DungeonDef, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array): void {
  for (let y = def.oy; y < def.oy + DUNGEON_SIZE; y++) {
    for (let x = def.ox; x < def.ox + DUNGEON_SIZE; x++) {
      const i = y * MAP_W + x;
      ground[i] = def.realm!.wall;
      obj[i] = 0;
      ring[i] = def.ring;
    }
  }
}

/**
 * The Hollow: a twisting maze (3-wide passages, a few loops), with chambers for the monster packs,
 * the entrance in the top-left and the Lich's arena in the bottom-right.
 */
function carveMaze(rng: () => number, def: DungeonDef, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array): Room[] {
  fillRealm(def, ground, obj, ring);
  const { ox, oy } = def;
  const floor = (x: number, y: number) => {
    const i = y * MAP_W + x;
    ground[i] = def.floor;
    obj[i] = 0;
  };
  const C = 5, N = Math.floor((DUNGEON_SIZE - 4) / C); // cell pitch: 3 floor + 2 wall
  const cx = (i: number) => ox + 2 + i * C, cy = (j: number) => oy + 2 + j * C;
  const seen = new Uint8Array(N * N);
  const open = (i: number, j: number) => { for (let y = 0; y < 3; y++) for (let x = 0; x < 3; x++) floor(cx(i) + x, cy(j) + y); };
  const link = (i: number, j: number, k: number, l: number) => {
    const x0 = Math.min(cx(i), cx(k)), y0 = Math.min(cy(j), cy(l));
    for (let y = y0; y < Math.max(cy(j), cy(l)) + 3; y++) for (let x = x0; x < Math.max(cx(i), cx(k)) + 3; x++) floor(x, y);
  };
  const stack: [number, number][] = [[0, 0]];
  seen[0] = 1;
  open(0, 0);
  while (stack.length) {
    const [i, j] = stack[stack.length - 1];
    const next = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const)
      .map(([dx, dy]) => [i + dx, j + dy] as [number, number])
      .filter(([k, l]) => k >= 0 && l >= 0 && k < N && l < N && !seen[l * N + k]);
    if (!next.length) {
      stack.pop();
      continue;
    }
    const [k, l] = next[Math.floor(rng() * next.length)];
    seen[l * N + k] = 1;
    open(k, l);
    link(i, j, k, l);
    stack.push([k, l]);
  }
  // Knock through some extra walls so there's more than one way round.
  for (let t = 0; t < N * N * 0.12; t++) {
    const i = Math.floor(rng() * (N - 1)), j = Math.floor(rng() * (N - 1));
    if (rng() < 0.5) link(i, j, i + 1, j);
    else link(i, j, i, j + 1);
  }
  const rooms: Room[] = [{ x: ox + 2, y: oy + 2, w: 13, h: 13 }];
  // Chambers spread over a 3x3 grid (minus the entrance and arena corners).
  const third = Math.floor(DUNGEON_SIZE / 3);
  for (const [gx, gy] of [[1, 0], [2, 0], [0, 1], [1, 1], [2, 1], [0, 2], [1, 2]]) {
    const w = 8 + Math.floor(rng() * 4), h = 8 + Math.floor(rng() * 4);
    rooms.push({ x: ox + gx * third + 3 + Math.floor(rng() * (third - w - 6)), y: oy + gy * third + 3 + Math.floor(rng() * (third - h - 6)), w, h });
  }
  rooms.push({ x: ox + DUNGEON_SIZE - 2 - 26, y: oy + DUNGEON_SIZE - 2 - 24, w: 26, h: 24 });
  for (const r of rooms) for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) floor(x, y);
  furnish(rng, def, ground, obj, rooms);
  return rooms;
}

/**
 * The Twilight Glade: round clearings in a thicket, joined by winding paths, dusky trees around
 * the clearings' edges and the dragon's big clearing last.
 */
function carveGlade(rng: () => number, def: DungeonDef, ground: Uint8Array, obj: Uint8Array, ring: Uint8Array): Room[] {
  fillRealm(def, ground, obj, ring);
  const { ox, oy } = def;
  const floor = (x: number, y: number) => {
    if (x < ox + 2 || y < oy + 2 || x >= ox + DUNGEON_SIZE - 2 || y >= oy + DUNGEON_SIZE - 2) return;
    const i = y * MAP_W + x;
    ground[i] = def.floor;
    obj[i] = 0;
  };
  const cell = Math.floor(DUNGEON_SIZE / 3);
  const blobs = SNAKE.map(([gx, gy], k) => {
    const r = k === SNAKE.length - 1 ? 13 : 6 + Math.floor(rng() * 4);
    const x = ox + gx * cell + Math.floor(cell / 2) + Math.floor((rng() - 0.5) * (cell - 2 * r - 6));
    const y = oy + gy * cell + Math.floor(cell / 2) + Math.floor((rng() - 0.5) * (cell - 2 * r - 6));
    return { x, y, r };
  });
  for (const b of blobs) {
    for (let y = b.y - b.r - 2; y <= b.y + b.r + 2; y++) {
      for (let x = b.x - b.r - 2; x <= b.x + b.r + 2; x++) {
        const a = Math.atan2(y - b.y, x - b.x);
        const edge = b.r * (0.85 + 0.15 * Math.sin(a * 3 + b.x) + 0.08 * Math.sin(a * 7 + b.y));
        if (Math.hypot(x - b.x, y - b.y) <= edge) floor(x, y);
      }
    }
  }
  // Winding paths: walk toward the next clearing with a sideways wobble, 4 tiles wide.
  const path = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    let x = a.x, y = a.y;
    for (let t = 0; t < 400 && Math.hypot(b.x - x, b.y - y) > 1.5; t++) {
      const ang = Math.atan2(b.y - y, b.x - x) + (rng() - 0.5) * 1.4;
      x += Math.cos(ang);
      y += Math.sin(ang);
      for (let dy = -2; dy <= 1; dy++) for (let dx = -2; dx <= 1; dx++) floor(Math.round(x) + dx, Math.round(y) + dy);
    }
  };
  for (let k = 1; k < blobs.length; k++) path(blobs[k - 1], blobs[k]);
  path(blobs[1], blobs[4]);
  path(blobs[3], blobs[8]);
  // Trees ring each clearing (never on the paths' line through the middle), flowers on the floor.
  for (const b of blobs.slice(1)) {
    for (let t = 0; t < b.r * 3; t++) {
      const a = rng() * Math.PI * 2, d = b.r * (0.62 + rng() * 0.2);
      const x = Math.round(b.x + Math.cos(a) * d), y = Math.round(b.y + Math.sin(a) * d);
      const i = y * MAP_W + x;
      let clear = true;
      for (let dy = -1; dy <= 1 && clear; dy++) for (let dx = -1; dx <= 1 && clear; dx++) if (ground[(y + dy) * MAP_W + x + dx] !== def.floor || obj[(y + dy) * MAP_W + x + dx]) clear = false;
      if (clear) obj[i] = OBJ.FEN_TREE;
    }
  }
  for (let y = oy; y < oy + DUNGEON_SIZE; y++) for (let x = ox; x < ox + DUNGEON_SIZE; x++) {
    const i = y * MAP_W + x;
    if (ground[i] === def.floor && !obj[i] && rng() < 0.06) obj[i] = rng() < 0.15 ? OBJ.BERRY_BUSH : OBJ.DECOR_FEN;
  }
  // The thicket's edge is a wall of dusky trees (standing on the solid ground, so they never block a path).
  for (let y = oy + 1; y < oy + DUNGEON_SIZE - 1; y++) for (let x = ox + 1; x < ox + DUNGEON_SIZE - 1; x++) {
    const i = y * MAP_W + x;
    if (ground[i] !== def.realm!.wall || obj[i]) continue;
    const edge = ground[i - 1] === def.floor || ground[i + 1] === def.floor || ground[i - MAP_W] === def.floor || ground[i + MAP_W] === def.floor;
    if (edge && rng() < 0.45) obj[i] = rng() < 0.7 ? OBJ.FEN_TREE : OBJ.PINE;
  }
  const rooms = blobs.map((b) => ({ x: b.x - Math.round(b.r * 0.6), y: b.y - Math.round(b.r * 0.6), w: Math.round(b.r * 1.2), h: Math.round(b.r * 1.2) }));
  furnish(rng, def, ground, obj, rooms, false);
  return rooms;
}

/** Lights in the room corners, pillars and rubble, and veins of the realm's ores. */
function furnish(rng: () => number, def: DungeonDef, ground: Uint8Array, obj: Uint8Array, rooms: Room[], ruinous = true): void {
  const put = (x: number, y: number, o: number) => {
    const i = y * MAP_W + x;
    if (ground[i] === def.floor && !obj[i]) obj[i] = o;
  };
  rooms.forEach((r, k) => {
    for (const [x, y] of [[r.x + 1, r.y + 1], [r.x + r.w - 2, r.y + 1], [r.x + 1, r.y + r.h - 2], [r.x + r.w - 2, r.y + r.h - 2]]) put(x, y, OBJ.BRAZIER);
    if (k === 0 || k === rooms.length - 1) return;
    const n = ruinous ? 2 + Math.floor(rng() * 3) : 0;
    for (let j = 0; j < n; j++) put(r.x + 2 + Math.floor(rng() * (r.w - 4)), r.y + 2 + Math.floor(rng() * (r.h - 4)), rng() < 0.5 ? OBJ.RUIN_PILLAR : OBJ.RUBBLE);
    const ores = 1 + Math.floor(rng() * 3);
    for (let j = 0; j < ores; j++) put(r.x + 2 + Math.floor(rng() * (r.w - 4)), r.y + 2 + Math.floor(rng() * (r.h - 4)), ORES[def.ring][Math.floor(rng() * 2)]);
  });
}
